import { observer } from "mobx-react";
import React, { useContext, useEffect } from "react";
import {
  Container,
  Stack,
  Title,
  Text,
  Box,
  Paper,
  List,
  ThemeIcon,
  Button,
  Group,
  CopyButton,
} from "@mantine/core";
import { IconCheck, IconLink, IconCopy } from "@tabler/icons-react";
import { DecodeInvoiceStoreContext } from "../stores/DecodeInvoiceStore";
import InvoiceInput from "./InvoiceInput";
import DecodedInvoiceDisplay from "./DecodedInvoiceDisplay";
import DecodeInvoiceBackground from "./DecodeInvoiceBackground";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../../helpers/ServerRenderedBlock";
import ToolLogo from "../../helpers/ToolLogo";

const DecodeInvoiceContainer: React.FC = observer(() => {
  const store = useContext(DecodeInvoiceStoreContext);

  // Get shareable URL for current invoice
  const getShareableUrl = (): string => {
    const baseUrl = window.location.origin + window.location.pathname;
    if (store.invoiceInput.trim()) {
      return `${baseUrl}?invoice=${encodeURIComponent(
        store.invoiceInput.trim()
      )}`;
    }
    return baseUrl;
  };

  // On mount: read invoice from URL and decode if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invoiceFromUrl = params.get("invoice");
    if (invoiceFromUrl) {
      store.setInvoiceInput(invoiceFromUrl);
      store.decodeInvoice();
    }
  }, []);

  // Update URL when invoice is successfully decoded
  useEffect(() => {
    if (store.decodedInvoice && store.invoiceInput.trim()) {
      const newUrl = getShareableUrl();
      // Use replaceState for the first decode, pushState for subsequent ones
      if (window.location.search.includes("invoice=")) {
        window.history.replaceState(
          { invoice: store.invoiceInput },
          "",
          newUrl
        );
      } else {
        window.history.pushState({ invoice: store.invoiceInput }, "", newUrl);
      }
    }
  }, [store.decodedInvoice]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.invoice) {
        store.setInvoiceInput(event.state.invoice);
        store.decodeInvoice();
      } else {
        // No invoice in state, check URL
        const params = new URLSearchParams(window.location.search);
        const invoiceFromUrl = params.get("invoice");
        if (invoiceFromUrl) {
          store.setInvoiceInput(invoiceFromUrl);
          store.decodeInvoice();
        } else {
          store.clear();
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return (
    <DecodeInvoiceBackground>
      <Container size={700} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {hasServerRenderedBlock("ssr-inv-head") ? (
            <ServerRenderedBlock id="ssr-inv-head" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                ⚡ BOLT11 Invoice Decoder
              </Title>
              <Text c="dimmed" size="sm">
                Paste a Lightning invoice to decode and inspect its contents
              </Text>
            </Box>
          )}

          <InvoiceInput />

          <DecodedInvoiceDisplay />

          {/* Copy Link section - show after successful decode */}
          {store.decodedInvoice && (
            <Paper
              p="md"
              radius="md"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <IconLink size={18} color="teal" />
                  <Text size="sm" c="dimmed">
                    Share this decoded invoice:
                  </Text>
                </Group>
                <CopyButton value={getShareableUrl()}>
                  {({ copied, copy }) => (
                    <Button
                      variant={copied ? "filled" : "outline"}
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      leftSection={
                        copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )
                      }
                      onClick={copy}
                    >
                      {copied ? "Link Copied!" : "Copy Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text
                size="xs"
                c="dimmed"
                mt="xs"
                style={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  opacity: 0.7,
                }}
              >
                {getShareableUrl()}
              </Text>
            </Paper>
          )}

          {/* SEO-friendly explanation section - hide after first successful decode */}
          {!store.decodedInvoice && (
            <Paper
              p="lg"
              radius="md"
              mt="xl"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Title order={3} c="white" mb="sm">
                Why We Built This Lightning Invoice Decoder
              </Title>
              <Text c="dimmed" size="sm" mb="md">
                Most BOLT11 decoders show raw data that's difficult to
                understand. We built this tool to make Lightning Network
                invoices human-readable, especially for developers and node
                operators who need to quickly verify invoice details.
              </Text>

              <Title order={4} c="white" mb="xs" size="sm">
                What Makes This Decoder Different
              </Title>
              <List
                spacing="xs"
                size="sm"
                c="dimmed"
                icon={
                  <ThemeIcon color="teal" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  <Text span c="white" fw={500}>
                    Node Aliases:
                  </Text>{" "}
                  We show the human-readable alias for destination nodes and
                  route hint nodes, not just cryptic 66-character public keys,
                  and whether the payee is a public node
                </List.Item>
                <List.Item>
                  <Text span c="white" fw={500}>
                    Clear Route Hints:
                  </Text>{" "}
                  Private invoices include routing hints that tell your wallet
                  how to reach the payee. We display these with node names, fee
                  rates, and channel IDs in an understandable format
                </List.Item>
                <List.Item>
                  <Text span c="white" fw={500}>
                    Live Expiry:
                  </Text>{" "}
                  A countdown while the invoice can still be paid, and a clear
                  "expired" warning once it can't
                </List.Item>
                <List.Item>
                  <Text span c="white" fw={500}>
                    Plain-Language Checks:
                  </Text>{" "}
                  Warns about testnet or signet invoices, invoices with no
                  amount, a missing payment secret, and payee nodes that aren't
                  in the public Lightning graph and have no route hints
                </List.Item>
                <List.Item>
                  <Text span c="white" fw={500}>
                    Amboss Integration:
                  </Text>{" "}
                  Click any public key to view the node's details on Amboss
                </List.Item>
              </List>

              <Text c="dimmed" size="xs" mt="md">
                Works with mainnet, testnet, and signet Lightning invoices. Your
                invoice data is processed securely and never stored.
              </Text>
            </Paper>
          )}


          <RizfulPitch
            lead="Need to make Lightning invoices, not just read them? Rizful creates them for you, from any browser."
          />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </DecodeInvoiceBackground>
  );
});

export default DecodeInvoiceContainer;
